/**
 * Clara 360 - CSV Match Review Component
 * Review and confirm matched transactions
 */

import { useState, useEffect } from 'react';
import { CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import csvMatchingService from '@/services/csvMatchingService';

export default function CsvMatchReview({ batchId, onClose, onComplete }) {
  const [matches, setMatches] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    loadMatches();
  }, [batchId]);

  async function loadMatches() {
    try {
      setLoading(true);
      const data = await csvMatchingService.getMatchedTransactions(batchId);
      setMatches(data);
      
      // Auto-select green matches (confidence >= 0.9)
      const autoSelect = new Set(
        data
          .filter(m => m.match_confidence >= 0.9)
          .map(m => m.id)
      );
      setSelected(autoSelect);
    } catch (error) {
      alert('Fehler beim Laden: ' + error.message);
    } finally {
      setLoading(false);
    }
  }

  function toggleSelect(id) {
    const newSelected = new Set(selected);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelected(newSelected);
  }

  function toggleAll() {
    if (selected.size === matches.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(matches.map(m => m.id)));
    }
  }

  async function confirmSelected() {
    if (selected.size === 0) return;
    
    if (!confirm(`${selected.size} Zahlungen bestätigen?`)) return;
    
    try {
      setConfirming(true);
      const result = await csvMatchingService.confirmMatches(Array.from(selected));
      
      let message = `✅ ${result.confirmed} Zahlungen bestätigt!`;
      if (result.learned > 0) {
        message += `\n🎯 ${result.learned} IBAN${result.learned > 1 ? 's' : ''} gespeichert – zukünftige Zahlungen werden automatisch erkannt`;
      }
      
      alert(message);
      onComplete?.();
      onClose?.();
    } catch (error) {
      alert('Fehler beim Bestätigen: ' + error.message);
    } finally {
      setConfirming(false);
    }
  }

  function getConfidenceColor(confidence) {
    if (confidence >= 0.9) return 'bg-green-50 border-green-200 text-green-700';
    if (confidence >= 0.7) return 'bg-yellow-50 border-yellow-200 text-yellow-700';
    return 'bg-red-50 border-red-200 text-red-700';
  }

  function getConfidenceIcon(confidence) {
    if (confidence >= 0.9) return <CheckCircle className="w-5 h-5 text-green-600" />;
    if (confidence >= 0.7) return <AlertCircle className="w-5 h-5 text-yellow-600" />;
    return <XCircle className="w-5 h-5 text-red-600" />;
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-DE', { 
      style: 'currency', 
      currency: 'EUR' 
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Lade Matches...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-7xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                🔍 Zahlungen zuordnen
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {matches.length} Vorschläge gefunden | {selected.size} ausgewählt
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {matches.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500 dark:text-gray-400">
                Keine Matches gefunden. Versuche andere Transaktionen oder passe Mieter-IBANs an.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {matches.map((match) => (
                <div
                  key={match.id}
                  className={`border-2 rounded-lg p-4 ${getConfidenceColor(match.match_confidence)} ${
                    selected.has(match.id) ? 'ring-2 ring-blue-500' : ''
                  }`}
                >
                  <div className="flex items-start gap-4">
                    {/* Checkbox */}
                    <input
                      type="checkbox"
                      checked={selected.has(match.id)}
                      onChange={() => toggleSelect(match.id)}
                      className="mt-1 w-5 h-5 rounded border-gray-300"
                    />

                    {/* Confidence Icon */}
                    <div className="flex-shrink-0">
                      {getConfidenceIcon(match.match_confidence)}
                    </div>

                    {/* Transaction Info */}
                    <div className="flex-1 grid grid-cols-3 gap-4">
                      <div>
                        <div className="text-xs font-medium opacity-70">Transaktion</div>
                        <div className="font-semibold">{match.transaction_date}</div>
                        <div className="text-lg font-bold">{formatCurrency(match.amount_abs)}</div>
                        <div className="text-sm mt-1">{match.sender_name || '-'}</div>
                        <div className="text-xs font-mono opacity-70">{match.sender_iban || '-'}</div>
                      </div>

                      <div>
                        <div className="text-xs font-medium opacity-70">Zugeordnet zu</div>
                        <div className="font-semibold">{match.suggested_tenant?.name || '-'}</div>
                        <div className="text-sm opacity-70">{match.suggested_tenant?.email || '-'}</div>
                        <div className="text-sm mt-2">
                          Soll: {formatCurrency(match.suggested_payment?.expected_amount)}
                        </div>
                      </div>

                      <div>
                        <div className="text-xs font-medium opacity-70 mb-1">
                          Warum? (Confidence: {Math.round(match.match_confidence * 100)}%)
                        </div>
                        <div className="text-sm whitespace-pre-line font-mono">
                          {match.match_reason}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <button
            onClick={toggleAll}
            className="text-sm text-blue-600 hover:text-blue-700"
          >
            {selected.size === matches.length ? 'Alle abwählen' : 'Alle auswählen'}
          </button>

          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={confirming}
              className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Abbrechen
            </button>
            <button
              onClick={confirmSelected}
              disabled={confirming || selected.size === 0}
              className="px-6 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
            >
              {confirming ? '⏳ Bestätige...' : `✅ ${selected.size} Zahlungen bestätigen`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
